import React from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { Submission, TableColumn } from '../../types/domain';
import { ColumnDataType, RBACPermissionTier } from '../../types/grid';
import { ShieldAlert, ShieldCheck, CheckCircle2, Eye, Trash2, Check, Copy } from 'lucide-react';
import { IndeterminateCheckbox } from './IndeterminateCheckbox';
import { Button } from '../ui/Button';

import { IdCell } from './IdCell';

interface BuildColumnsParams {
  configuredColumns: TableColumn[];
  submissions: Submission[];
  userTier: RBACPermissionTier;
  enableSelection?: boolean;
  onInspectRow: (row: Submission) => void;
  onDeleteRow: (rowId: string) => void;
}

export function inferColumnType(values: any[]): ColumnDataType {
  for (const v of values) {
    if (v === null || v === undefined) continue;
    if (typeof v === 'boolean') return 'boolean';
    if (typeof v === 'number') return 'number';
    if (typeof v === 'object') {
      if (v && ('file_id' in v || 'filename' in v)) return 'file';
      if (Array.isArray(v) && v.length > 0 && typeof v[0] === 'object' && v[0] && ('file_id' in v[0] || 'filename' in v[0])) {
        return 'file';
      }
      return 'json';
    }
    if (typeof v === 'string') {
      if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) return 'email';
      if (/^https?:\/\//.test(v)) return 'url';
      if (!isNaN(Number(v)) && v.trim() !== '') return 'number';
      if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(v)) return 'date';
    }
  }
  return 'text';
}

export function buildGridColumns({
  configuredColumns,
  submissions,
  userTier,
  enableSelection = false,
  onInspectRow,
  onDeleteRow,
}: BuildColumnsParams): ColumnDef<Submission, any>[] {
  const canEdit = userTier === 'edit' || userTier === 'full';

  const cols: ColumnDef<Submission, any>[] = [
    // 1. [x] + ID (Selection + ID combined, cannot be hidden)
    {
      id: 'select',
      accessorFn: (row) => row.id,
      size: 110,
      enableSorting: false,
      enableHiding: false,
      meta: {
        key: 'id',
        dataType: 'text',
      },
      header: ({ table }) => (
        <div className="flex items-center gap-2 px-1">
          {enableSelection && (
            <IndeterminateCheckbox
              checked={table.getIsAllPageRowsSelected()}
              indeterminate={table.getIsSomePageRowsSelected()}
              onChange={table.getToggleAllPageRowsSelectedHandler()}
              ariaLabel="Select all rows"
            />
          )}
          <span className="font-sans text-xs font-semibold text-cf-textSecondary">ID</span>
        </div>
      ),
      cell: ({ row }) => (
        <div className="flex items-center gap-2 px-1">
          {enableSelection && (
            <IndeterminateCheckbox
              checked={row.getIsSelected()}
              disabled={!row.getCanSelect()}
              indeterminate={row.getIsSomeSelected()}
              onChange={row.getToggleSelectedHandler()}
              ariaLabel={`Select row ${row.index + 1}`}
            />
          )}
          <IdCell id={row.original.id} />
        </div>
      ),
    },
  ];

  // 4. Configured Columns from database
  const configuredKeys = new Set<string>();
  const sortedConfigured = [...configuredColumns].sort((a, b) => a.position - b.position);

  for (const col of sortedConfigured) {
    configuredKeys.add(col.key);
    cols.push({
      id: `data_${col.key}`,
      accessorFn: (row) => row.data?.[col.key],
      header: col.name || col.key,
      size: Math.min(Math.max(col.width || 180, 120), 280),
      maxSize: 280,
      minSize: 120,
      enableSorting: true,
      meta: {
        key: col.key,
        dataType: (col.data_type || 'text') as ColumnDataType,
        isRequired: col.is_required,
        isDynamic: false,
      },
    });
  }

  // 5. Dynamic JSONB columns discovery from loaded submissions
  const dynamicKeys = new Set<string>();
  for (const row of submissions) {
    if (!row.data) continue;
    for (const key of Object.keys(row.data)) {
      if (!configuredKeys.has(key) && !key.startsWith('_')) {
        dynamicKeys.add(key);
      }
    }
  }

  // Add discovered dynamic columns
  for (const key of Array.from(dynamicKeys).sort()) {
    const sampleValues = submissions.map((s) => s.data?.[key]);
    const inferredType = inferColumnType(sampleValues);

    cols.push({
      id: `data_${key}`,
      accessorFn: (row) => row.data?.[key],
      header: () => (
        <span className="flex items-center gap-1.5" title="Discovered from JSONB payload">
          <span>{key}</span>
          <span className="text-xs px-1 py-0.2 rounded bg-cf-orangeMuted text-cf-orange border border-cf-orange/20">
            AUTO
          </span>
        </span>
      ),
      size: 180,
      maxSize: 280,
      minSize: 120,
      enableSorting: true,
      meta: {
        key,
        dataType: inferredType,
        isDynamic: true,
      },
    });
  }

  // 6. Created At Timestamp
  cols.push({
    id: 'created_at',
    accessorKey: 'created_at',
    header: 'Ingested At',
    size: 160,
    maxSize: 170,
    minSize: 150,
    enableSorting: true,
    cell: (info) => {
      const val = info.getValue() as string;
      if (!val) return null;
      return (
        <span
          className="text-zinc-400 text-xs tabular-nums whitespace-nowrap truncate block"
          title={new Date(val).toISOString()}
        >
          {new Date(val).toISOString().replace('T', ' ').slice(0, 19)}
        </span>
      );
    },
  });

  // 7. Status Badge: OK (true) vs SPAM (false) (cannot be hidden)
  cols.push({
    id: 'status',
    accessorFn: (row) => (row.is_spam || row.status === 'quarantined' ? 'SPAM' : 'OK'),
    header: 'Status',
    size: 85,
    enableSorting: true,
    enableHiding: false,
    meta: {
      key: 'status',
      dataType: 'status' as ColumnDataType,
      options: [
        { value: 'OK', label: 'OK', color: 'emerald' },
        { value: 'SPAM', label: 'SPAM', color: 'rose' },
      ],
      isSystem: false,
    },
    cell: (info) => {
      const row = info.row.original;
      const isSpam = row.is_spam || row.status === 'quarantined';
      return isSpam ? (
        <div className="flex items-center gap-1.5 whitespace-nowrap" title="Spam trapped by honeypot">
          <ShieldAlert size={13} className="text-rose-500 dark:text-rose-400 flex-shrink-0" />
          <span className="text-xs font-medium text-cf-textPrimary">SPAM</span>
        </div>
      ) : (
        <div className="flex items-center gap-1.5 whitespace-nowrap" title="Clean submission">
          <CheckCircle2 size={13} className="text-emerald-500 dark:text-emerald-400 flex-shrink-0" />
          <span className="text-xs font-medium text-cf-textPrimary">OK</span>
        </div>
      );
    },
  });

  // 8. Actions Column
  cols.push({
    id: 'actions',
    header: 'Actions',
    size: 100,
    enableSorting: false,
    enableHiding: false,
    cell: (info) => (
      <div className="flex items-center justify-end gap-1 px-1">
        <Button
          type="button"
          variant="ghost"
          size="icon-xs"
          onClick={(e) => {
            e.stopPropagation();
            onInspectRow(info.row.original);
          }}
          className="text-cf-textSecondary hover:text-cf-textPrimary"
          title="Inspect row details & JSON"
        >
          <Eye size={14} />
        </Button>

        {canEdit && (
          <Button
            type="button"
            variant="ghost"
            size="icon-xs"
            onClick={(e) => {
              e.stopPropagation();
              onDeleteRow(info.row.original.id);
            }}
            className="text-cf-textSecondary hover:text-rose-500"
            title="Delete submission row"
          >
            <Trash2 size={14} />
          </Button>
        )}
      </div>
    ),
  });

  return cols;
}
